/**
 * The Credit Assessment Report (finding #5), as the business defined it in the Tirhelo Credit Assessment
 * Procedure Manual rev01 and the Credit Assessment Report Template rev0. The structure, the
 * calculations and the checks live here, apart from the screen, so they can be tested and changed
 * in one place. The report is saved as a flat map of field key to value.
 */

export type ReportValues = Record<string, string | number | boolean | undefined>;

export type FieldKind = 'text' | 'longtext' | 'money' | 'date' | 'select';

export interface ReportField {
  key: string;
  label: string;
  kind: FieldKind;
  options?: string[];
  hint?: string;
}

/** One line of a section's calculation, worked out from the fields. */
export interface ReportCalculation {
  label: string;
  value: (v: ReportValues) => number | undefined;
  /** Highlight a negative result (a deficit or shortfall). */
  signed?: boolean;
}

export interface ReportRow {
  key: string;
  label: string;
}

export interface ReportSection {
  key: string;
  title: string;
  /** The question the section answers, from the template. */
  question?: string;
  fields?: ReportField[];
  /** Rows of a control or threshold table, each with the row fields below. */
  rows?: ReportRow[];
  rowFields?: ReportField[];
  calculations?: ReportCalculation[];
  /** Ends in an outcome (Pass / Pass with mitigation / Fail / Hard stop / N/A). Overlays do not. */
  outcome?: boolean;
}

export const OUTCOMES = ['PASS', 'PASS_WITH_MITIGATION', 'FAIL', 'HARD_STOP', 'NA'] as const;
export type Outcome = typeof OUTCOMES[number];
export const OUTCOME_LABELS: Record<Outcome, string> = {
  PASS: 'Pass',
  PASS_WITH_MITIGATION: 'Pass with mitigation',
  FAIL: 'Fail',
  HARD_STOP: 'Hard stop',
  NA: 'N/A',
};

export const DECISIONS = ['APPROVE', 'APPROVE_WITH_CONDITIONS', 'ESCALATE', 'DECLINE'] as const;
export type Decision = typeof DECISIONS[number];
export const DECISION_LABELS: Record<Decision, string> = {
  APPROVE: 'Approve',
  APPROVE_WITH_CONDITIONS: 'Approve with conditions',
  ESCALATE: 'Escalate / policy exception',
  DECLINE: 'Decline',
};

/** 11.8 Conditions precedent, from the manual. */
export const CONDITIONS_PRECEDENT = [
  'Applicant contribution',
  'Direct supplier payment',
  'Cession',
  'Bank account control',
  'CSD banking control',
  'Asset registration',
  'Insurance',
  'Security perfection',
  'Confirmation of additional funding',
];

/** The manual's recognised collateral value: 70% of the verified retail value (7.1). A report keeps the share
 * set in Settings when it was started ('s7.recognisedPercent', finding #29); older reports use 70%. */
export const VEHICLE_RECOGNISED_PERCENT = 70;

const EVIDENCE_STATUS = ['Received', 'Partly received', 'Outstanding', 'Not applicable'];
const CONTROL_STATUS = ['Required', 'Not required', 'N/A'];
const SECURITY_STATUS = ['Required', 'In place', 'Not required', 'N/A'];
const THRESHOLD_STATUS = ['Compliant', 'Exception required', 'Breach / not permitted', 'Not assessed'];
const RISK_RATING = ['Low', 'Moderate', 'High'];

export const num = (v: ReportValues, key: string): number | undefined => {
  const raw = v[key];
  if (raw === undefined || raw === '' || raw === null) return undefined;
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[\s,R]/g, ''));
  return Number.isFinite(n) ? n : undefined;
};

/** Adds the amounts given; undefined until at least one is filled in. */
const sum = (...values: (number | undefined)[]): number | undefined =>
  values.some(x => x !== undefined) ? values.reduce<number>((acc, x) => acc + (x ?? 0), 0) : undefined;

export const finding = (key: string, label = 'Key finding'): ReportField => ({ key, label, kind: 'longtext' });

export const SECTIONS: ReportSection[] = [
  {
    key: 'pre', title: 'Pre-assessment: evidence readiness',
    question: 'Do we have enough information?',
    rows: [
      { key: 'bankStatements', label: 'Bank statements' },
      { key: 'transactionValue', label: 'Transaction value & costs' },
      { key: 'offtakerEvidence', label: 'Payer / offtaker evidence' },
      { key: 'capability', label: 'Proof client can do the work' },
      { key: 'security', label: 'Security & repayment controls' },
      { key: 'clientHistory', label: 'Tirhelo client history' },
      { key: 'underlying', label: 'Underlying contract / PO / invoice' },
      { key: 'other', label: 'Other evidence needed for this deal' },
    ],
    rowFields: [{ key: 'status', label: 'Status', kind: 'select', options: EVIDENCE_STATUS }],
  },
  {
    key: 'snapshot', title: 'Application / deal snapshot',
    fields: [
      { key: 'applicant', label: 'Applicant / business', kind: 'text' },
      { key: 'registrationNo', label: 'Registration no.', kind: 'text' },
      { key: 'purpose', label: 'Facility purpose', kind: 'text' },
      { key: 'underlying', label: 'Underlying contract / PO / invoice', kind: 'text' },
      { key: 'transactionValue', label: 'Transaction / contract value', kind: 'money' },
      { key: 'repaymentSource', label: 'Primary repayment source', kind: 'text' },
      { key: 'amountRequested', label: 'Facility amount requested', kind: 'money' },
      { key: 'proposedTerm', label: 'Proposed term', kind: 'text' },
      { key: 'clientType', label: 'Client type', kind: 'select', options: ['New client', 'Existing client'] },
      { key: 'offtaker', label: 'Offtaker / payer', kind: 'text' },
      { key: 'expectedRepaymentDate', label: 'Expected repayment date', kind: 'date' },
      { key: 'reference', label: 'Application / reference no.', kind: 'text' },
      { key: 'assessor', label: 'Credit assessor', kind: 'text' },
      { key: 'assessmentDate', label: 'Assessment date', kind: 'date' },
      { key: 'referralSource', label: 'Referral source', kind: 'select', options: ['Direct', 'Referral', 'Broker / agent', 'Existing client', 'Other'] },
      { key: 'referralDetail', label: 'Referral detail / referrer', kind: 'text' },
    ],
  },
  {
    key: 's1_1', title: '1.1 Bank statement financial analysis', outcome: true,
    question: 'What do the bank statements tell us?',
    fields: [
      { key: 'avgInflows', label: 'Average monthly trading inflows', kind: 'money' },
      { key: 'otherIncome', label: 'Other sustainable business income', kind: 'money' },
      { key: 'avgOutflows', label: 'Average monthly operating outflows', kind: 'money' },
      { key: 'commitments', label: 'Existing financial commitments', kind: 'money' },
      { key: 'flags', label: 'Bank statement flags / stress indicators', kind: 'longtext' },
      finding('finding', '1.1 Key finding'),
    ],
    calculations: [{
      label: 'Estimated normal monthly cash surplus / (deficit)', signed: true,
      value: v => {
        const inflow = sum(num(v, 's1_1.avgInflows'), num(v, 's1_1.otherIncome'));
        const outflow = sum(num(v, 's1_1.avgOutflows'), num(v, 's1_1.commitments'));
        return inflow === undefined && outflow === undefined ? undefined : (inflow ?? 0) - (outflow ?? 0);
      },
    }],
  },
  {
    key: 's1_2', title: '1.2 Project / transaction financial & repayment analysis', outcome: true,
    question: 'Do the numbers support repayment and a profit?',
    fields: [
      { key: 'expectedReceived', label: 'Expected amount to be received (contract / PO / invoice)', kind: 'money', hint: 'After known contractual deductions; not automatically the headline value.' },
      { key: 'totalCost', label: 'Total cost to complete the transaction', kind: 'money' },
      { key: 'applicantFunding', label: 'Applicant + other confirmed funding', kind: 'money' },
      { key: 'tirheloAmount', label: 'Amount assessed for Tirhelo funding', kind: 'money' },
      { key: 'tirheloCharges', label: 'Tirhelo interest & charges', kind: 'money' },
      finding('finding', '1.2 Key finding'),
    ],
    calculations: [
      { label: 'Total amount to repay Tirhelo', value: v => sum(num(v, 's1_2.tirheloAmount'), num(v, 's1_2.tirheloCharges')) },
      {
        label: 'Expected profit / (loss) after Tirhelo charges', signed: true,
        value: v => num(v, 's1_2.expectedReceived') === undefined ? undefined
          : (num(v, 's1_2.expectedReceived') ?? 0) - (num(v, 's1_2.totalCost') ?? 0) - (num(v, 's1_2.tirheloCharges') ?? 0),
      },
      {
        label: 'Funding left over / (shortfall)', signed: true,
        value: v => num(v, 's1_2.totalCost') === undefined ? undefined
          : (num(v, 's1_2.applicantFunding') ?? 0) + (num(v, 's1_2.tirheloAmount') ?? 0) - (num(v, 's1_2.totalCost') ?? 0),
      },
    ],
  },
  {
    key: 's2_1', title: '2.1 External credit profile', outcome: true,
    question: 'What is the main credit concern?',
    fields: [
      { key: 'businessScore', label: 'Business credit score / risk band', kind: 'text' },
      { key: 'directorProfiles', label: 'Relevant director(s) profile', kind: 'longtext', hint: 'The lowest director score / highest risk band is normally the starting position.' },
      { key: 'adverse', label: 'Material arrears / defaults / judgments / adverse listings', kind: 'longtext' },
      { key: 'distress', label: 'Material debt / financial distress concerns', kind: 'longtext' },
      finding('finding', '2.1 Key finding'),
      { key: 'override', label: 'If overriding the weakest director result, explain why', kind: 'longtext' },
    ],
  },
  {
    key: 's2_2', title: '2.2 Tirhelo repayment & conduct history', outcome: true,
    question: 'How has the client behaved with Tirhelo?',
    fields: [
      { key: 'previousFacilities', label: 'Previous Tirhelo facilities / value', kind: 'text' },
      { key: 'repayment', label: 'Repayment performance', kind: 'longtext' },
      { key: 'extensions', label: 'Extensions / late settlement / additional interest', kind: 'longtext' },
      { key: 'conduct', label: 'Communication & conduct', kind: 'longtext' },
      finding('finding', '2.2 Key finding'),
    ],
  },
  { key: 's3_1', title: '3.1 Transaction structure & dependency risk', outcome: true, question: 'What could stop completion or payment?', fields: [finding('finding')] },
  { key: 's3_2', title: '3.2 Offtaker risk', outcome: true, question: 'Can the payer / offtaker pay?', fields: [finding('finding')] },
  { key: 's3_3', title: '3.3 Repayment timing & timing risk', outcome: true, question: 'Will repayment arrive within the funding term?', fields: [finding('finding')] },
  { key: 's3_4', title: '3.4 Project complexity', outcome: true, question: 'Is the project / transaction complexity manageable?', fields: [finding('finding')] },
  {
    key: 's4', title: '4. Execution viability', outcome: true,
    question: 'Can this client actually deliver the work?',
    fields: [
      { key: 'experience', label: 'Relevant experience / similar work', kind: 'longtext' },
      { key: 'capability', label: 'Management & technical capability', kind: 'longtext' },
      { key: 'resources', label: 'Resources available for this job', kind: 'longtext' },
      { key: 'workload', label: 'Current workload & ability to start the job', kind: 'longtext' },
      finding('finding', 'Section 4 key finding'),
    ],
  },
  {
    key: 's5', title: '5. How Tirhelo will control the loan money', outcome: true,
    rows: [
      { key: 'directPayment', label: 'Direct supplier / manufacturer / subcontractor payment' },
      { key: 'staged', label: 'Staged / milestone disbursement' },
      { key: 'proof', label: 'Proof of delivery / purchase' },
      { key: 'controlled', label: 'Controlled payment instruction / other control' },
    ],
    rowFields: [
      { key: 'status', label: 'Status', kind: 'select', options: CONTROL_STATUS },
      { key: 'how', label: 'How it will work / proof', kind: 'text' },
    ],
    fields: [finding('finding', 'Overall finding')],
  },
  {
    key: 's6', title: '6. Alternative repayment sources & competing commitments',
    question: 'Overlay: carry any material effect to sections 1 and 4; not rated here.',
    rows: [
      { key: 'r1', label: 'Source / commitment 1' },
      { key: 'r2', label: 'Source / commitment 2' },
      { key: 'r3', label: 'Source / commitment 3' },
    ],
    rowFields: [
      { key: 'what', label: 'Other income / commitment', kind: 'text' },
      { key: 'amount', label: 'Expected amount', kind: 'money' },
      { key: 'when', label: 'When expected', kind: 'text' },
      { key: 'effect', label: 'Effect', kind: 'select', options: ['Supportive', 'Neutral', 'Adverse'] },
      { key: 'how', label: 'How this helps or reduces repayment', kind: 'text' },
    ],
  },
  {
    key: 's7', title: '7. Security & repayment protection', outcome: true,
    question: 'Verification has already checked the security; this assesses whether it protects the facility.',
    rows: [
      { key: 'vehicle', label: '7.1 Vehicle security' },
      { key: 'csd', label: '7.2 CSD bank detail control' },
      { key: 'account', label: '7.3 Bank account control' },
      { key: 'cession', label: '7.4 Cession of payment / receivable' },
      { key: 'title', label: '7.5 Asset / title control' },
      { key: 'other', label: 'Other security / repayment control' },
    ],
    rowFields: [
      { key: 'status', label: 'Status', kind: 'select', options: SECURITY_STATUS },
      { key: 'position', label: 'Current position', kind: 'text' },
      { key: 'condition', label: 'Required condition / control', kind: 'text' },
    ],
    fields: [
      { key: 'retailValue', label: 'Verified vehicle retail value', kind: 'money' },
      { key: 'existingExposure', label: 'Existing Tirhelo funded exposure (incl. related borrowers)', kind: 'money' },
      finding('finding', 'Overall security finding'),
      { key: 'mitigation', label: 'Mitigation / condition', kind: 'longtext' },
    ],
    calculations: [
      { label: 'Recognised vehicle security value (share of retail value from Settings)', value: v => vehicleRecognisedValue(v) },
      { label: 'Exposure after this loan', value: v => exposureAfterLoan(v) },
      {
        label: 'Security cover left / (shortfall)', signed: true,
        value: v => vehicleRecognisedValue(v) === undefined ? undefined : (vehicleRecognisedValue(v) ?? 0) - (exposureAfterLoan(v) ?? 0),
      },
    ],
  },
  {
    key: 's8', title: '8. Connected client relationship (existing clients only)',
    question: 'Overlay: may support reasonable flexibility, never reverses a Fail or Hard stop.',
    fields: [
      { key: 'length', label: 'How long has the client been with Tirhelo?', kind: 'text' },
      { key: 'status', label: 'Relationship status', kind: 'select', options: ['Strong positive', 'Established / neutral', 'Weak / deteriorating', 'Not applicable (new client)'] },
      { key: 'effect', label: 'How this relationship affects the assessment', kind: 'longtext' },
    ],
  },
  {
    key: 's9', title: '9. Credit & portfolio thresholds',
    rows: [
      { key: 'client', label: '9.1 Single client exposure' },
      { key: 'offtaker', label: '9.2 Offtaker concentration' },
      { key: 'portfolio', label: '9.3 Portfolio effect / diversification' },
      { key: 'capacity', label: '9.4 Available lending capacity' },
    ],
    rowFields: [
      { key: 'position', label: 'Position / effect', kind: 'text' },
      { key: 'limit', label: 'Limit / capacity', kind: 'text' },
      { key: 'status', label: 'Status', kind: 'select', options: THRESHOLD_STATUS },
      { key: 'comment', label: 'Comment', kind: 'text' },
    ],
  },
  {
    key: 's10', title: '10. Overall risk assessment',
    question: 'Summarises the sections above; not a second score.',
    rows: [
      { key: 'financial', label: 'Financial & repayment' },
      { key: 'behavioural', label: 'Behavioural' },
      { key: 'transaction', label: 'Transaction / offtaker / timing' },
      { key: 'execution', label: 'Execution' },
      { key: 'funds', label: 'Funds control & security' },
      { key: 'portfolio', label: 'Portfolio / exposure' },
    ],
    rowFields: [
      { key: 'rating', label: 'Rating', kind: 'select', options: RISK_RATING },
      { key: 'finding', label: 'Key finding / mitigation', kind: 'text' },
    ],
  },
  {
    key: 's11', title: '11. Credit recommendation',
    fields: [
      { key: 'amount', label: '11.1 Recommended facility amount', kind: 'money' },
      { key: 'term', label: '11.2 Recommended facility term', kind: 'text' },
      { key: 'pricing', label: '11.3 Pricing / interest', kind: 'text' },
      { key: 'charges', label: 'Interest & charges', kind: 'money' },
      { key: 'totalRepay', label: '11.4 Total amount to repay Tirhelo', kind: 'money', hint: 'From 1.2' },
      { key: 'primarySource', label: '11.5 Primary repayment source', kind: 'text' },
      { key: 'primaryAmount', label: 'Primary repayment expected amount', kind: 'money' },
      { key: 'primaryDate', label: 'Primary repayment expected date', kind: 'date' },
      { key: 'contract', label: 'Related contract / PO / invoice', kind: 'text' },
      { key: 'offtaker', label: 'Offtaker', kind: 'text' },
      { key: 'altSource', label: '11.6 Alternative repayment source', kind: 'text' },
      { key: 'altAmountTiming', label: 'Alternative source expected amount / timing', kind: 'text' },
      { key: 'controls', label: '11.7 Final security & repayment controls', kind: 'longtext' },
      { key: 'otherCondition', label: 'Other condition before payment', kind: 'text' },
      { key: 'rationale', label: 'Decision rationale', kind: 'longtext' },
      { key: 'approver', label: 'Approver / approval authority', kind: 'text' },
      { key: 'approvalDate', label: 'Approval date', kind: 'date' },
    ],
  },
];

export function vehicleRecognisedValue(v: ReportValues): number | undefined {
  const retail = num(v, 's7.retailValue');
  const percent = num(v, 's7.recognisedPercent') ?? VEHICLE_RECOGNISED_PERCENT;
  return retail === undefined ? undefined : Math.round(retail * percent) / 100;
}

/** Existing funded exposure plus this facility (the assessed Tirhelo amount, else the amount requested). */
export function exposureAfterLoan(v: ReportValues): number | undefined {
  const thisLoan = num(v, 's1_2.tirheloAmount') ?? num(v, 'snapshot.amountRequested');
  return sum(num(v, 's7.existingExposure'), thisLoan);
}

export const outcomeSections = () => SECTIONS.filter(s => s.outcome);

export type OverallResult = 'INCOMPLETE' | 'HARD_STOP' | 'FAIL' | 'PASS_WITH_MITIGATION' | 'PASS';
export const OVERALL_LABELS: Record<OverallResult, string> = {
  INCOMPLETE: 'Assessment incomplete',
  HARD_STOP: 'Hard stop',
  FAIL: 'Fail recorded',
  PASS_WITH_MITIGATION: 'Pass with mitigation',
  PASS: 'Pass',
};

/** The overall result from the section outcomes, worst first. */
export function overallResult(v: ReportValues): OverallResult {
  const outcomes = outcomeSections().map(s => v[`${s.key}.outcome`] as Outcome | undefined);
  if (outcomes.includes('HARD_STOP')) return 'HARD_STOP';
  if (outcomes.some(o => !o)) return 'INCOMPLETE';
  if (outcomes.includes('FAIL')) return 'FAIL';
  if (outcomes.includes('PASS_WITH_MITIGATION')) return 'PASS_WITH_MITIGATION';
  return 'PASS';
}

export function evidenceStatus(v: ReportValues): 'Not started' | 'Ready' | 'Outstanding evidence' | 'Partly ready' {
  const pre = SECTIONS.find(s => s.key === 'pre')!;
  const statuses = (pre.rows || []).map(r => v[`pre.${r.key}.status`] as string | undefined);
  if (statuses.every(s => !s)) return 'Not started';
  if (statuses.some(s => s === 'Outstanding')) return 'Outstanding evidence';
  if (statuses.every(s => s === 'Received' || s === 'Not applicable')) return 'Ready';
  return 'Partly ready';
}

export const conditionKey = (condition: string) => `s11.condition.${condition}`;

/** The template's decision consistency check: contradictions between the outcomes and the decision. */
export function consistencyIssues(v: ReportValues): string[] {
  const decision = v['s11.decision'] as Decision | undefined;
  if (!decision) return [];
  const result = overallResult(v);
  const conditions = CONDITIONS_PRECEDENT.filter(c => v[conditionKey(c)]).length + (v['s11.otherCondition'] ? 1 : 0);
  const issues: string[] = [];
  const approving = decision === 'APPROVE' || decision === 'APPROVE_WITH_CONDITIONS';
  if (result === 'INCOMPLETE') issues.push('Some sections have no outcome yet: complete them before deciding.');
  if (result === 'HARD_STOP' && approving) issues.push('A Hard stop cannot be approved. Decline, or escalate if the hard stop is disputed.');
  if (result === 'FAIL' && decision === 'APPROVE') issues.push('A section failed: record the mitigation and approve with conditions, escalate or decline.');
  if (decision === 'APPROVE' && conditions > 0) issues.push('Conditions before payment are listed: choose "Approve with conditions".');
  if (decision === 'APPROVE_WITH_CONDITIONS' && conditions === 0) issues.push('List the conditions before payment (11.8).');
  if (approving && !v['s11.amount']) issues.push('Record the recommended facility amount (11.1).');
  if (approving && !v['s11.approver']) issues.push('Record the approver / approval authority.');
  return issues;
}

/** What the report starts with, from the application: the assessor completes the rest. */
export function prefillFromApplication(app: any, assessor?: string): ReportValues {
  const d = app?.loanDetails || {};
  const c = app?.company || {};
  const today = new Date().toISOString().slice(0, 10);
  const values: ReportValues = {
    'snapshot.applicant': c.registeredName || c.companyName || c.tradingName || '',
    'snapshot.registrationNo': c.registrationNumber || c.companyRegNumber || '',
    'snapshot.purpose': d.assetPurpose || d.purpose || '',
    'snapshot.amountRequested': d.loanAmount ?? '',
    'snapshot.transactionValue': d.projectValue ?? '',
    'snapshot.offtaker': d.projectClientName || '',
    'snapshot.expectedRepaymentDate': d.projectExpectedPaymentDate || d.invoiceExpectedPaymentDate || d.bridgeExpectedPaymentDate || d.dueDate || '',
    'snapshot.proposedTerm': d.numberOfInstallments && d.numberOfInstallments > 1 ? `${d.numberOfInstallments} months` : '',
    'snapshot.reference': app?.applicationNumber || '',
    'snapshot.assessor': assessor || '',
    'snapshot.assessmentDate': today,
    's11.amount': d.loanAmount ?? '',
  };
  Object.keys(values).forEach(k => { if (values[k] === '' || values[k] === undefined) delete values[k]; });
  return values;
}
